/**
 * Framework Selection Page (System)
 * Which regulations apply to us?
 */
import React, { useState } from 'react';
import { 
  ListChecks, 
  CheckCircle2, 
  Circle,
  AlertTriangle,
  Calendar,
  Info
} from 'lucide-react';

const FRAMEWORKS = [
  {
    id: 'csrd',
    name: 'CSRD / ESRS',
    description: 'EU Corporate Sustainability Reporting Directive',
    type: 'mandatory',
    enabled: true,
    applicableFrom: '2024-01-01',
    status: 'active',
  },
  {
    id: 'csddd',
    name: 'CSDDD (CS3D)',
    description: 'Corporate Sustainability Due Diligence Directive',
    type: 'mandatory',
    enabled: true,
    applicableFrom: '2026-01-01',
    status: 'upcoming',
  },
  {
    id: 'eu-taxonomy',
    name: 'EU Taxonomy',
    description: 'Sustainable activities classification',
    type: 'mandatory',
    enabled: true,
    applicableFrom: '2022-01-01',
    status: 'active',
  },
  {
    id: 'cbam',
    name: 'CBAM',
    description: 'Carbon Border Adjustment Mechanism',
    type: 'mandatory',
    enabled: false,
    applicableFrom: '2026-01-01',
    status: 'upcoming',
  },
  {
    id: 'tcfd',
    name: 'TCFD',
    description: 'Task Force on Climate-related Financial Disclosures',
    type: 'voluntary',
    enabled: true,
    applicableFrom: null,
    status: 'active',
  },
  {
    id: 'cdp',
    name: 'CDP',
    description: 'Climate, Water, Forests Disclosure',
    type: 'voluntary',
    enabled: true,
    applicableFrom: null,
    status: 'active',
  },
  {
    id: 'gri',
    name: 'GRI Standards',
    description: 'Global Reporting Initiative',
    type: 'voluntary',
    enabled: true,
    applicableFrom: null,
    status: 'active',
  },
  {
    id: 'sbti',
    name: 'SBTi',
    description: 'Science Based Targets initiative',
    type: 'voluntary',
    enabled: false,
    applicableFrom: null,
    status: 'inactive',
  },
];

const TYPE_CONFIG = {
  mandatory: { label: 'Mandatory', bg: 'bg-red-500/20', text: 'text-red-400' },
  voluntary: { label: 'Voluntary', bg: 'bg-blue-500/20', text: 'text-blue-400' },
};

const STATUS_CONFIG = {
  active: { label: 'Active', bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
  upcoming: { label: 'Upcoming', bg: 'bg-amber-500/20', text: 'text-amber-400' },
  inactive: { label: 'Not Enabled', bg: 'bg-[#2F2F2F]', text: 'text-[#6B6B6B]' },
};

export default function FrameworkSelectionPage() {
  const [frameworks, setFrameworks] = useState(FRAMEWORKS);

  const toggleFramework = (id) => {
    setFrameworks(prev => prev.map(fw => 
      fw.id === id ? { ...fw, enabled: !fw.enabled } : fw
    ));
  };

  const enabledCount = frameworks.filter(fw => fw.enabled).length;
  const mandatoryEnabled = frameworks.filter(fw => fw.type === 'mandatory' && fw.enabled).length;

  return (
    <div className="space-y-6" data-testid="framework-selection-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Framework Selection</h1>
        <p className="text-sm text-[#6B6B6B] mt-1">
          Select which regulatory frameworks and standards apply to your organization
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <CheckCircle2 size={18} className="text-emerald-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-emerald-400">{enabledCount}</div>
              <div className="text-xs text-[#6B6B6B]">Enabled</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/20">
              <AlertTriangle size={18} className="text-red-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{mandatoryEnabled}</div>
              <div className="text-xs text-[#6B6B6B]">Mandatory</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <ListChecks size={18} className="text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{FRAMEWORKS.length}</div>
              <div className="text-xs text-[#6B6B6B]">Available</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Calendar size={18} className="text-amber-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-400">
                {frameworks.filter(fw => fw.status === 'upcoming').length}
              </div>
              <div className="text-xs text-[#6B6B6B]">Upcoming</div>
            </div>
          </div>
        </div>
      </div>

      {/* Mandatory Frameworks */}
      <div>
        <h3 className="text-sm font-semibold text-[#6B6B6B] uppercase tracking-wider mb-3">
          Mandatory Reporting
        </h3>
        <div className="space-y-3">
          {frameworks.filter(fw => fw.type === 'mandatory').map(fw => {
            const typeConfig = TYPE_CONFIG[fw.type];
            const statusConfig = STATUS_CONFIG[fw.enabled ? fw.status : 'inactive'];
            
            return (
              <div 
                key={fw.id} 
                className={`bg-[#111111] border ${fw.enabled ? 'border-emerald-500/30' : 'border-[#1F1F1F]'} rounded-xl p-4 transition-all`}
                data-testid={`framework-${fw.id}`}
              >
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => toggleFramework(fw.id)}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                      fw.enabled ? 'bg-emerald-500 border-emerald-500' : 'border-[#4A4A4A] hover:border-emerald-500'
                    }`}
                  >
                    {fw.enabled && <CheckCircle2 size={14} className="text-white" />}
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-white">{fw.name}</h4>
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${typeConfig.bg} ${typeConfig.text}`}>
                        {typeConfig.label}
                      </span>
                      <span className={`px-2 py-0.5 text-[10px] font-medium rounded ${statusConfig.bg} ${statusConfig.text}`}>
                        {statusConfig.label}
                      </span>
                    </div>
                    <p className="text-xs text-[#6B6B6B]">{fw.description}</p>
                  </div>
                  {fw.applicableFrom && (
                    <div className="text-right">
                      <span className="text-xs text-[#6B6B6B]">Applicable from</span>
                      <p className="text-sm text-white">{new Date(fw.applicableFrom).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Voluntary Frameworks */}
      <div>
        <h3 className="text-sm font-semibold text-[#6B6B6B] uppercase tracking-wider mb-3">
          Voluntary Commitments
        </h3>
        <div className="space-y-3">
          {frameworks.filter(fw => fw.type === 'voluntary').map(fw => {
            const typeConfig = TYPE_CONFIG[fw.type];
            const statusConfig = STATUS_CONFIG[fw.enabled ? 'active' : 'inactive'];
            
            return (
              <div 
                key={fw.id} 
                className={`bg-[#111111] border ${fw.enabled ? 'border-blue-500/30' : 'border-[#1F1F1F]'} rounded-xl p-4 transition-all`}
                data-testid={`framework-${fw.id}`}
              >
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => toggleFramework(fw.id)}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                      fw.enabled ? 'bg-blue-500 border-blue-500' : 'border-[#4A4A4A] hover:border-blue-500'
                    }`}
                  >
                    {fw.enabled && <CheckCircle2 size={14} className="text-white" />}
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-white">{fw.name}</h4>
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${typeConfig.bg} ${typeConfig.text}`}>
                        {typeConfig.label}
                      </span>
                      <span className={`px-2 py-0.5 text-[10px] font-medium rounded ${statusConfig.bg} ${statusConfig.text}`}>
                        {statusConfig.label}
                      </span>
                    </div>
                    <p className="text-xs text-[#6B6B6B]">{fw.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex items-start gap-3">
        <Info size={18} className="text-blue-400 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="text-sm font-medium text-white mb-1">Framework Configuration</h4>
          <p className="text-xs text-[#A0A0A0]">
            Enabling a framework will activate its specific data requirements, evidence mappings, and reporting templates. 
            Mandatory frameworks cannot be fully disabled if your organization meets the scope thresholds.
          </p>
        </div>
      </div>
    </div>
  );
}
